import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';
import FeatureIcon, { ICON_TONES, IconTone } from '../../components/FeatureIcon';
import ContextualMedia from '../../components/ContextualMedia';
import GarageSpecEvidence from './GarageSpecEvidence';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../../lib/screenshotPreview';
import { selectedCaptureSrc } from '../../lib/selectedCaptures';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const photoHeading = '#f8fafc';
const photoBody = 'rgba(226,232,240,0.9)';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const Garage: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const deviceBorder = theme === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)';
  const shot = (screen: string) =>
    `/assets/screenshots/ios-delivery/Garage/iOS - Garage - ${screen} - ${theme === 'dark' ? 'Dark' : 'Light'}.png`;

  const deviceShot = (screen: string, alt: string, caption: string, width = 'w-56 sm:w-64') => (
    <figure className={`${width} max-w-full min-w-0 m-0`}>
      <div className="rounded-[1.6rem] border-4 overflow-hidden shadow-2xl" style={{ borderColor: deviceBorder }}>
        <img src={shot(screen)} alt={alt} className="w-full" loading="lazy" width={1320} height={2868} />
      </div>
      <figcaption className="text-xs mt-3 text-center" style={{ color: heroSecondary }}>
        {caption}
      </figcaption>
    </figure>
  );

  // Selected-capture variant: resolves the optimized derivative + srcset.
  const selectedShot = (key: 'garageDetail', alt: string, caption: string, width = 'w-56 sm:w-64') => {
    const src = selectedCaptureSrc(key, theme);
    return (
      <figure className={`${width} max-w-full min-w-0 m-0`}>
        <div className="rounded-[1.6rem] border-4 overflow-hidden shadow-2xl" style={{ borderColor: deviceBorder }}>
          <img src={getScreenshotPreview(src)} srcSet={getScreenshotPreviewSet(src)} sizes="(min-width: 640px) 256px, 224px" alt={alt} className="w-full" loading="lazy" width={1320} height={2868} />
        </div>
        <figcaption className="text-xs mt-3 text-center" style={{ color: heroSecondary }}>
          {caption}
        </figcaption>
      </figure>
    );
  };

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  const managingFeatures = [
    { title: 'Choose for the Event', items: ['Add your motorcycle first', 'Pick the bike you\'ll bring when you join', 'Appears on that event\'s roster'] },
    { title: 'Primary Bike', items: ['Your default bike', 'Shows first in your garage', 'Stays visible on your public profile'] },
    { title: 'Garage Capacity', items: ['Free: 3 bikes max', 'Pro: 5 bikes max', 'Room for the whole stable'] }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* 1 · Hero — garage motorcycle; copy on the quiet left wall */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <div className="md:hidden aspect-[16/10] bg-cover bg-[position:60%_center]" style={{ backgroundImage: `url('/assets/images/ridepulse-garage-motorcycle-hero-v1.png')` }} role="img" aria-label="Motorcycle parked inside a garage" />
        <div
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(100deg, rgba(8,10,16,0.72) 0%, rgba(8,10,16,0.5) 45%, rgba(8,10,16,0.15) 72%, transparent 88%), url('/assets/images/ridepulse-garage-motorcycle-hero-v1.png')`
          }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-32 lg:py-40">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <p className={eyebrow} style={{ color: dsColors.primary }}>Your Garage</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Your Digital Garage
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 leading-relaxed" style={{ color: photoBody, textWrap: 'pretty' }}>
              Add your motorcycle, then choose the bike you'll bring when you join an event.
            </p>
            <div className="flex justify-center md:justify-start gap-4 flex-wrap">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Add & bring — participation trio */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <AnimatedSection>
            <ContextualMedia
              mediaSide="left"
              media={selectedShot(
                'garageDetail',
                'Red Rock, a 2023 Aprilia RS 660, shown in RidePulse with its details and owner-approved illustration',
                'A motorcycle in your Garage.'
              )}
            >
              <div className="mb-8">
                <p className={eyebrow} style={{ color: dsColors[ICON_TONES.community] }}>Participation</p>
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Add your bike, bring it to events.</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Every participant picks a motorcycle. Your garage is where that choice lives.
                </p>
              </div>
              <div className="garage-feature-cards">
                {managingFeatures.map((f, i) => (
                  <div key={f.title} className="p-6 rounded-xl text-left" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                    <span
                      className="inline-flex w-10 h-10 rounded-xl items-center justify-center mb-4"
                      style={{
                        backgroundColor: `${dsColors[ICON_TONES[(['community', 'attention', 'neutral'] as IconTone[])[i]]]}1f`,
                        color: dsColors[ICON_TONES[(['community', 'attention', 'neutral'] as IconTone[])[i]]],
                      }}
                      aria-hidden="true"
                    >
                      <FeatureIcon name={(['calendar', 'star', 'layers'] as const)[i]} />
                    </span>
                    <h3 className="text-xl font-bold mb-4" style={{ color: heroPrimary }}>{f.title}</h3>
                    <ul className="space-y-2">
                      {f.items.map((item) => (
                        <li key={item} className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors[ICON_TONES.community] }} aria-hidden="true" />
                          <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </ContextualMedia>
          </AnimatedSection>
        </div>
      </section>

      {/* 3 · Specs close at hand — controlled detail under the copy */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="spec-layout">
              <div className="max-w-2xl">
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Your bike's specs, close at hand.</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Select your motorcycle to keep useful specifications close, including torque, front and rear tire sizes, fuel capacity and weight.
                </p>
                <p className="mt-4 text-sm" style={{ color: heroSecondary, opacity: 0.8 }}>
                  Available specifications vary by model and year.
                </p>
              </div>
              <GarageSpecEvidence />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Adding a Bike — benefit callout linking to the guide (steps live in /help) */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="rounded-2xl p-8 md:p-10" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
              <ContextualMedia
                media={deviceShot(
                  'Add Motorcycle',
                  'The add-motorcycle form in RidePulse with a selected bike summary, photo option, and detail fields',
                  'Adding a motorcycle.'
                )}
              >
                <div>
                  <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Adding a Bike From Our Database</h2>
                  <p className={`${sectionLead}`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    Select your brand, year, model, and variation. We handle the rest: every step narrows down to your exact bike.
                  </p>
                  <p className="mt-6">
                    <Link
                      to="/help#add-motorcycle"
                      className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                      style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                    >
                      See how it works: the four-step add flow →
                    </Link>
                  </p>
                </div>
              </ContextualMedia>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5 · Make it yours — identity split with editorial panel */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <ContextualMedia
              media={deviceShot(
                'Edit Vehicle',
                'The update-vehicle form in RidePulse showing photo controls, nickname, license plate, and mileage fields',
                'Editing your motorcycle.'
              )}
            >
              <div>
                <p className={eyebrow} style={{ color: dsColors.primary }}>Identity</p>
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Make it yours.</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  A photo, a color, a nickname. Your garage shows your bikes the way you see them, and helps other riders recognize what's coming.
                </p>
                <ul className="mt-8 space-y-3">
                  {['A photo that says it\'s yours', 'Color and card theme', 'A nickname riders remember'].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors.primary }} aria-hidden="true" />
                      <span className="text-base" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <div
                  className="mt-10 garage-support-photo"
                  style={{ backgroundImage: `url('/assets/images/aprilia-rs660-red-rock-sunset-side.png')`, border: `1px solid ${cardBorder}` }}
                  role="img"
                  aria-label="Aprilia RS660 side profile against Red Rock mountains at sunset"
                />
              </div>
            </ContextualMedia>
          </AnimatedSection>
        </div>
      </section>

      {/* 6 · Privacy — connected layers: profile controls, per-bike levels, event roster */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <p className={eyebrow} style={{ color: dsColors[ICON_TONES.safety] }}>Privacy</p>
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Your visibility, in layers.</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Choose how additional motorcycles appear on your profile. When you join an event, the bike you bring becomes part of that event's participant information.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: heroPrimary }}>On your public profile</h3>
                <p className="text-sm mb-4" style={{ color: heroSecondary }}>
                  Your profile settings control which additional bikes and details show. Each bike also gets its own level (Public, Friends Only, or Hidden), with one exception: your primary bike stays visible on your profile.
                </p>
                <ul className="space-y-2">
                  {['Public: visible on your profile', 'Friends Only: visible to confirmed friends', 'Hidden: not shown on your profile'].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors[ICON_TONES.safety] }} aria-hidden="true" />
                      <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: heroPrimary }}>At an event</h3>
                <p className="text-sm mb-4" style={{ color: heroSecondary }}>
                  The motorcycle you select appears on the event's participant roster: make, model, year, and photo. Your public-profile bike settings don't hide it there; that's how riders know what's coming.
                </p>
                <ul className="space-y-2">
                  {['The bike you bring is part of the event', 'Hidden on your profile ≠ hidden at the event'].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: dsColors[ICON_TONES.safety] }} aria-hidden="true" />
                      <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-8 text-sm text-center" style={{ color: heroSecondary }}>
              Change any bike's profile visibility anytime. Profile controls in context:{' '}
              <Link
                to="/social#profile-privacy"
                className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Community: profile privacy →
              </Link>
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* 7 · Close — one quiet line; the three jobs were each covered above */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
          <AnimatedSection>
            <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Your bike is part of the plan.</h2>
            <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Add it once. Bring it to events, keep its specs at hand, and decide who sees it.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Garage;
